import { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { useWhatsApp } from '../context/WhatsAppContext';
import './WhatsAppModal.css';

emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'GDBe01r_UR6ZU8fQU');

function formatPhoneNumber(value) {
  const phoneNumber = value.replace(/\D/g, '');
  if (!phoneNumber) return '';
  if (phoneNumber.length > 10) {
    return phoneNumber.slice(0, 10).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
}

async function sendEmail(formData, recaptchaToken) {
  try {
    if (!process.env.REACT_APP_EMAILJS_SERVICE_ID ||
        !process.env.REACT_APP_EMAILJS_TEMPLATE_ID ||
        !process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      console.warn('EmailJS not properly configured');
      return { success: false, message: 'Email service not configured.' };
    }

    const cleanPhone = formData.phone.replace(/\D/g, '');

    const templateParams = {
      from_name: formData.name,
      from_phone: cleanPhone,
      to_email: process.env.REACT_APP_EMAIL_TO || 'Teninpalli@gmail.com',
      message: `New WhatsApp group join request from ${formData.name} (${cleanPhone}). Please add this person to the WhatsApp group within 24-48 hours.`,
      reply_to: cleanPhone,
      'g-recaptcha-response': recaptchaToken
    };

    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );

    console.log('Email sent successfully:', result.text);
    return { success: true, message: 'Email sent successfully!' };
  } catch (error) {
    console.error('Email sending failed:', error);
    return { success: false, message: 'Failed to send email notification.' };
  }
}

export default function WhatsAppModal() {
  const { isOpen, close } = useWhatsApp();
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    window.onRecaptchaSuccess = (token) => {
      setRecaptchaToken(token);
      setRecaptchaError('');
    };

    const renderRecaptcha = (retry = false) => {
      try {
        if (window.grecaptcha && window.grecaptcha.render) {
          const recaptchaElement = document.querySelector('.g-recaptcha');
          if (recaptchaElement && !recaptchaElement.hasChildNodes()) {
            window.grecaptcha.render(recaptchaElement, {
              sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
              callback: 'onRecaptchaSuccess',
              'error-callback': () => {
                console.log('reCAPTCHA error occurred');
                setRecaptchaError('reCAPTCHA failed to load. Please refresh the page and try again.');
              },
              'expired-callback': () => {
                setRecaptchaToken(null);
                setRecaptchaError('reCAPTCHA expired. Please complete it again.');
              },
              'timeout-callback': () => {
                console.log('reCAPTCHA timeout occurred');
                setRecaptchaError('reCAPTCHA timed out. Please try again.');
              }
            });
          }
        } else if (!retry) {
          console.log('reCAPTCHA not loaded yet, retrying...');
          setTimeout(() => renderRecaptcha(true), 1000);
        }
      } catch (error) {
        console.log('reCAPTCHA render error:', error);
        setRecaptchaError('reCAPTCHA failed to initialize. Please refresh the page and try again.');
      }
    };

    const t = setTimeout(() => renderRecaptcha(false), 300);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason === 'Timeout') {
        console.log('reCAPTCHA timeout - ignoring promise rejection');
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const closeModal = () => {
    close();
    setFormData({ name: '', phone: '' });
    setRecaptchaToken(null);
    setRecaptchaError('');
    setSubmitMessage('');
    setIsSubmitting(false);
    try {
      if (window.grecaptcha && window.grecaptcha.reset) {
        window.grecaptcha.reset();
      }
    } catch (error) {
      console.log('reCAPTCHA reset error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: formatPhoneNumber(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim()) {
      alert('Please fill in all fields.');
      return;
    }

    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('Please enter a valid US phone number in the format (123) 456-7890');
      return;
    }

    if (!recaptchaToken) {
      setRecaptchaError('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const emailResult = await sendEmail(formData, recaptchaToken);
      if (emailResult.success) {
        setSubmitMessage('✅ Your request has been submitted successfully! You will be added to our WhatsApp group within 24-48 hours.');
      } else {
        setSubmitMessage('⚠️ Your request has been submitted, but there was an issue with the notification. You will still be added within 24-48 hours.');
      }

      setTimeout(() => {
        close();
        setFormData({ name: '', phone: '' });
        setRecaptchaToken(null);
        setRecaptchaError('');
        setSubmitMessage('');
        setIsSubmitting(false);
        try {
          if (window.grecaptcha && window.grecaptcha.reset) {
            window.grecaptcha.reset();
          }
        } catch (error) {
          console.log('reCAPTCHA reset error:', error);
        }
      }, 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('❌ An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wa-modal-overlay" onClick={closeModal} role="presentation">
      <div
        className="wa-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-modal-title"
      >
        <button className="wa-modal__close" onClick={closeModal} aria-label="Close">×</button>
        <div className="wa-modal__header">
          <span className="wa-modal__eyebrow">Stay in the loop</span>
          <h2 id="wa-modal-title">Join Our WhatsApp Group</h2>
          <p>Daily specials, fresh-cut alerts, weekend menus.</p>
        </div>

        <form onSubmit={handleFormSubmit} className="wa-modal__form">
          <label className="wa-field">
            <span>Full Name</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your name"
              required
            />
          </label>

          <label className="wa-field">
            <span>Phone Number</span>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(123) 456-7890"
              maxLength="14"
              required
            />
          </label>

          <div className="wa-recaptcha">
            <div
              className="g-recaptcha"
              data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
              data-callback="onRecaptchaSuccess"
            ></div>
            {recaptchaError && (
              <div className="wa-error">{recaptchaError}</div>
            )}
          </div>

          {submitMessage && (
            <div
              className={
                'wa-message ' + (
                  submitMessage.includes('✅') ? 'wa-message--ok' :
                  submitMessage.includes('⚠️') ? 'wa-message--warn' :
                  'wa-message--err'
                )
              }
            >
              {submitMessage}
            </div>
          )}

          <button type="submit" className="btn btn-gold wa-modal__submit" disabled={isSubmitting}>
            {isSubmitting ? '⏳ Submitting…' : '📱 Request to Join'}
          </button>
        </form>
      </div>
    </div>
  );
}
